// src/components/ecommerce/ProductGrid.jsx
import React, { useState } from 'react';
import { Package } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { ProductCard } from './ProductCard';
import { ProductListItem } from './ProductListItem';

export const ProductGrid = ({ 
  products, 
  bookData,
  storeView = 'grid', 
  onProductClick, 
  onAddToCart,
  styling = {},
  storeSettings = {},
  className = '' 
}) => {
  const { addToCart, isInCart, getCartItem } = useCart();
  const [loadingItems, setLoadingItems] = useState({});

  // Extract design settings
  const design = bookData?.design || {};
  const gridColumns = design.gridColumns || 3;
  const fontSource = styling?.fontFamily || design?.fontFamily || bookData?.storeSettings?.fontFamily || 'Inter, sans-serif';
  const fontFamily = fontSource.includes(',') ? fontSource : `"${fontSource}", sans-serif`;

  const handleAddToCart = async (product, quantity = 1, variant = null) => {
    setLoadingItems(prev => ({ ...prev, [product.id]: true }));
    
    try {
      await addToCart(product, quantity, variant);
      onAddToCart?.(product, quantity, variant);
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      setLoadingItems(prev => ({ ...prev, [product.id]: false }));
    }
  };

  const getGridClasses = () => {
    switch (storeView) {
      case 'list':
        return 'space-y-4';
      case 'masonry':
        return 'columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-5';
      case 'grid':
      default:
        // Dynamic grid columns based on design settings
        const columnClasses = {
          1: 'grid-cols-1',
          2: 'grid-cols-1 md:grid-cols-2',
          3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
          4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
          5: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5',
          6: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6'
        };
        return `grid w-full ${columnClasses[gridColumns] || columnClasses[3]} gap-5`;
    }
  };

  if (!products || products.length === 0) {
    return (
      <div 
        className="text-center py-12"
        style={{ fontFamily: fontFamily }}
      >
        <Package className="mx-auto h-16 w-16 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
        <p className="text-gray-600">Check back later for new arrivals!</p>
      </div>
    );
  }

  return (
    <div 
      className={`w-full max-w-full overflow-x-hidden ${getGridClasses()} ${className}`}
      style={{ fontFamily: fontFamily }}
      
    >
      {products.map((product) => (
        storeView === 'list' ? (
          <ProductListItem
            key={product.id}
            product={product}
            onClick={() => onProductClick?.(product)}
            onAddToCart={handleAddToCart}
            isInCart={isInCart(product.id)}
            cartItem={getCartItem(product.id)}
            loading={loadingItems[product.id]}
            styling={styling}
            storeSettings={storeSettings}
          />
        ) : (
          <ProductCard
            key={product.id}
            product={product}
            bookData={bookData}
            storeView={storeView}
            onClick={() => onProductClick?.(product)}
            onAddToCart={handleAddToCart}
            isInCart={isInCart(product.id)}
            cartItem={getCartItem(product.id)}
            loading={loadingItems[product.id]}
            styling={styling}
            design={design}
            storeSettings={storeSettings}
          />
        )
      ))}
    </div>
  );
};
